import React from 'react';
import { Navigate } from 'react-router';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';

interface FeatureGateProps {
    /** Single flag that must be enabled */
    flag?: string;
    /** Multiple flags — at least one must be enabled */
    anyOf?: string[];
    /** Where to redirect if the flag is not enabled (route-level gating) */
    redirectTo?: string;
    /** Fallback content if the flag is not enabled (inline gating) */
    fallback?: React.ReactNode;
    children: React.ReactNode;
}

/**
 * Conditionally renders children based on feature flags.
 *
 * Route-level usage:
 *   <FeatureGate flag="mock_ui.farm" redirectTo="/documents">
 *     <FarmPage />
 *   </FeatureGate>
 *
 * Inline usage:
 *   <FeatureGate flag="twin.entities" fallback={<MockView />}>
 *     <LiveView />
 *   </FeatureGate>
 *
 * Multiple flags (OR):
 *   <FeatureGate anyOf={["mock_ui.farm", "twin.entities"]}>
 *     <FarmPage />
 *   </FeatureGate>
 */
const FeatureGate: React.FC<FeatureGateProps> = ({
    flag,
    anyOf,
    redirectTo,
    fallback = null,
    children,
}) => {
    const { hasFlag, hasAnyFlag, isLoaded } = useFeatureFlags();

    // Wait until flags are loaded to avoid flash
    if (!isLoaded) {
        return null;
    }

    const enabled = flag
        ? hasFlag(flag)
        : anyOf
            ? hasAnyFlag(anyOf)
            : false;

    if (!enabled) {
        if (redirectTo) {
            return <Navigate to={redirectTo} replace />;
        }
        return <>{fallback}</>;
    }

    return <>{children}</>;
};

export default FeatureGate;
